// Surfaces: the containers and status marks screens are built from.
import "./config";
import { Text, View, XStack, YStack, styled } from "tamagui";
import { Pressable } from "./controls";
import { ContourLines } from "./Contours";
import { Icon } from "./Icon";
import { Column } from "./layout";
import type { PaletteKey } from "./palette";
import { Body } from "./text";

export const Card = styled(YStack, {
  name: "Card",
  backgroundColor: "surface",
  borderRadius: "3",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  padding: "4",
  gap: "3",
  variants: {
    inset: { true: { backgroundColor: "surface2", borderColor: "transparent" } },
    accent: { true: { backgroundColor: "accentSoft", borderColor: "accentLine" } },
    flush: { true: { padding: 0, overflow: "hidden" } },
  } as const,
});

/** A card that is itself a button: the whole surface is the target. */
export const PressableCard = styled(Pressable, {
  name: "PressableCard",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  width: "100%",
  gap: "3",
  padding: "4",
  borderRadius: "3",
  borderWidth: "var(--borderW)",
  borderColor: { default: "line", hover: "lineStrong" },
  backgroundColor: { default: "surface", press: "surface2" },
  textAlign: "left",
  variants: {
    accent: { true: { backgroundColor: "accentSoft", borderColor: "accentLine" } },
  } as const,
});

const HeroFrame = styled(YStack, {
  name: "Hero",
  position: "relative",
  overflow: "hidden",
  padding: "6",
  gap: "3",
  borderRadius: "4",
  backgroundColor: "surface",
  borderWidth: "var(--borderW)",
  borderColor: "line",
});

export interface HeroProps extends React.ComponentProps<typeof HeroFrame> {
  /** The cartographic contour lines behind the content (hidden in the accessible version). */
  contours?: boolean;
}

/** The one hero surface: a roomy card the screen's headline sits on. */
export function Hero({ contours = true, children, ...rest }: HeroProps): React.JSX.Element {
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <HeroFrame style={{ isolation: "isolate" }} {...rest}>
      {contours ? (
        <View
          position="absolute"
          top={-20}
          right={-30}
          width="78%"
          opacity={{ default: 0.14, a11yLight: 0, a11yDark: 0 }}
          pointerEvents="none"
          color="ink"
          style={{ zIndex: -1 }}
          aria-hidden
        >
          <ContourLines />
        </View>
      ) : null}
      {children}
    </HeroFrame>
  );
}

const BannerFrame = styled(XStack, {
  name: "Banner",
  alignItems: "center",
  borderRadius: "2",
  padding: "3",
  gap: "3",
  borderWidth: "var(--borderW)",
  variants: {
    tone: {
      error: { backgroundColor: "errorBg", borderColor: "error" },
      ready: { backgroundColor: "readyBg", borderColor: "ready" },
      wait: { backgroundColor: "waitBg", borderColor: "wait" },
      info: { backgroundColor: "surface2", borderColor: "line" },
    },
  } as const,
  defaultVariants: { tone: "info" },
});

const BANNER = {
  error: { icon: "alert", colour: "error" },
  ready: { icon: "check", colour: "ready" },
  wait: { icon: "warning", colour: "wait" },
  info: { icon: "info", colour: "textDim" },
} as const;

export interface BannerProps extends Omit<React.ComponentProps<typeof BannerFrame>, "tone"> {
  tone?: keyof typeof BANNER;
  /** The message; richer content (a retry button under it) goes in children. */
  message?: string;
}

/** A status line: an error, a notice, something ready or waiting. Errors are announced at once. */
export function Banner({
  tone = "info",
  message,
  children,
  ...rest
}: BannerProps): React.JSX.Element {
  const { icon, colour } = BANNER[tone];
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <BannerFrame tone={tone} role={tone === "error" ? "alert" : "status"} {...rest}>
      <Icon name={icon} size={18} tone={colour} />
      <YStack flex={1} minWidth={0} gap="2">
        {message === undefined ? null : (
          <Body size="small" weight="medium" tone={tone === "info" ? "normal" : tone}>
            {message}
          </Body>
        )}
        {children}
      </YStack>
    </BannerFrame>
  );
}

/** What a failed load or save says, in the one red the app uses for it. */
export function ErrorBanner({
  message,
  ...rest
}: { message: string } & Omit<BannerProps, "tone" | "message">): React.JSX.Element {
  // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
  return <Banner tone="error" message={message} {...rest} />;
}

/** A small solid count or marker. */
export const Badge = styled(Text, {
  name: "Badge",
  render: "span",
  fontFamily: "body",
  fontSize: "1",
  fontWeight: "700",
  paddingHorizontal: "2",
  paddingVertical: 2,
  borderRadius: "pill",
  overflow: "hidden",
  color: "accentInk",
  backgroundColor: "accent",
  variants: {
    tone: {
      accent: { color: "accentInk", backgroundColor: "accent" },
      ready: { color: "ready", backgroundColor: "readyBg" },
      wait: { color: "wait", backgroundColor: "waitBg" },
      error: { color: "error", backgroundColor: "errorBg" },
      quiet: { color: "textDim", backgroundColor: "surface2" },
    },
  } as const,
});

/**
 * A state in words: ready, waiting, quiet, highlighted, failed. Softly tinted; the accessible
 * version draws it solid, so it never relies on a tint alone.
 */
export const Status = styled(Text, {
  name: "Status",
  render: "span",
  display: "inline-flex",
  alignItems: "center",
  gap: "1",
  fontFamily: "body",
  fontSize: "1",
  fontWeight: "700",
  letterSpacing: 0.3,
  paddingHorizontal: "2",
  paddingVertical: 4,
  borderRadius: "pill",
  whiteSpace: "nowrap",
  variants: {
    tone: {
      ok: {
        color: { default: "ready", a11yLight: "bg", a11yDark: "bg" },
        backgroundColor: { default: "readyBg", a11yLight: "ready", a11yDark: "ready" },
      },
      wait: {
        color: { default: "wait", a11yLight: "bg", a11yDark: "bg" },
        backgroundColor: { default: "waitBg", a11yLight: "wait", a11yDark: "wait" },
      },
      error: {
        color: { default: "error", a11yLight: "bg", a11yDark: "bg" },
        backgroundColor: { default: "errorBg", a11yLight: "error", a11yDark: "error" },
      },
      accent: {
        color: { default: "accent", a11yLight: "accentInk", a11yDark: "accentInk" },
        backgroundColor: { default: "accentSoft", a11yLight: "accent", a11yDark: "accent" },
      },
      lilac: {
        color: { default: "lilac", a11yLight: "accentInk", a11yDark: "accentInk" },
        backgroundColor: { default: "surface2", a11yLight: "accent", a11yDark: "accent" },
      },
      muted: {
        color: { default: "textDim", a11yLight: "text", a11yDark: "text" },
        backgroundColor: { default: "surface2", a11yLight: "bg", a11yDark: "bg" },
        borderWidth: { default: 0, a11yLight: 2, a11yDark: 2 },
        borderColor: "lineStrong",
      },
    },
  } as const,
  defaultVariants: { tone: "muted" },
});

/** The small round swatch before a wine colour's name; the name is always written beside it. */
export function ColourDot({ tone }: { tone: PaletteKey }): React.JSX.Element {
  return (
    <View
      width={10}
      height={10}
      borderRadius="pill"
      flexShrink={0}
      borderWidth={1}
      borderColor="lineStrong"
      backgroundColor={tone}
      aria-hidden
    />
  );
}

export interface ProgressBarProps {
  /** 0 to 1. */
  value: number;
  /** The bar's accessible name. */
  label: string;
}

/** A thin determinate bar; the fill animates between values. */
export function ProgressBar({ value, label }: ProgressBarProps): React.JSX.Element {
  const fraction = Math.max(0, Math.min(1, value));
  return (
    <View
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fraction * 100)}
      height={{ default: 6, a11yLight: 10, a11yDark: 10 }}
      borderRadius="pill"
      overflow="hidden"
      backgroundColor="surface3"
      borderWidth={{ default: 0, a11yLight: 2, a11yDark: 2 }}
      borderColor="lineStrong"
    >
      <View
        height="100%"
        borderRadius="pill"
        backgroundColor="accent"
        transitionProperty="width"
        transitionDuration="var(--durSlow)"
        style={{ width: `${String(fraction * 100)}%` }}
      />
    </View>
  );
}

const SKELETON = {
  line: { height: 12, borderRadius: 6 },
  row: { height: 64, borderRadius: 16 },
  card: { height: 140, borderRadius: 16 },
} as const;

/** A placeholder while something loads; it shimmers, and stops after two passes when `finite`. */
export function Skeleton({
  kind = "row",
  finite = false,
}: {
  kind?: keyof typeof SKELETON;
  finite?: boolean;
}): React.JSX.Element {
  return (
    <View
      className="ui-shimmer"
      backgroundColor="surface2"
      aria-hidden
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...SKELETON[kind]}
      style={finite ? { animationIterationCount: 2 } : undefined}
    />
  );
}

/** The action area at the foot of a flow screen: the primary button stays under the thumb. */
export function ActionBar({
  children,
  ...rest
}: React.ComponentProps<typeof Column>): React.JSX.Element {
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <Column className="ui-action-bar" gap="3" paddingTop="4" paddingBottom="1" {...rest}>
      {children}
    </Column>
  );
}

const PictureFrame = styled(View, { name: "Picture", render: "img" });

export interface PictureProps extends React.ComponentProps<typeof PictureFrame> {
  src: string;
  /** What the picture shows; `""` marks it decorative. */
  alt: string;
}

/** A raster picture. */
export function Picture({ src, alt, ...rest }: PictureProps): React.JSX.Element {
  // Tamagui's props do not list the img attributes, so they travel as a spread.
  const native = { src, alt };
  // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
  return <PictureFrame {...native} {...rest} />;
}
